import { Injectable } from '@angular/core';
import config from '../../config/config.json';

/** Config key for Regulation Next (INS_id redirect to regulation-details). */
const REGULATION_NEXT_CONFIG = (config as { RegulationNext?: { privilege?: string; defaultVersion?: string } }).RegulationNext;

/**
 * Privilege name for "Regulations Next" module (View Regulation).
 * When user has this privilege, INS_id links open the new regulation-details URL.
 * Read from config.json RegulationNext.privilege, fallback 'gri.regulation.read'.
 */
export const REGULATION_NEXT_PRIVILEGE = REGULATION_NEXT_CONFIG?.privilege ?? 'gri.regulation.read';

/**
 * Default version query param for Regulation Next URLs.
 * Read from config.json RegulationNext.defaultVersion, fallback '2.2'.
 */
export const REGULATION_NEXT_DEFAULT_VERSION = REGULATION_NEXT_CONFIG?.defaultVersion ?? '2.2';

/** Numeric regulation version, e.g. `3`, `3.0`, `1.2.1`. Non-numeric values ("Revision 1") are ignored. */
const NUMERIC_VERSION_PATTERN = /^\d+(\.\d+)*$/;

/**
 * Overall regulation version of a metadata item (flat, or nested under `metadata`).
 * Empty when missing or non-numeric, so callers keep the versionless link.
 */
export function resolveRegulationVersion(item: unknown): string {
  if (!item || typeof item !== 'object' || Array.isArray(item)) {
    return '';
  }
  const record = item as Record<string, unknown>;
  const nested = record['metadata'];
  const nestedRecord = nested && typeof nested === 'object' && !Array.isArray(nested)
    ? nested as Record<string, unknown>
    : {};
  const value = record['version'] ?? nestedRecord['version'];

  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }
  const trimmed = typeof value === 'string' ? value.trim() : '';
  return NUMERIC_VERSION_PATTERN.test(trimmed) ? trimmed : '';
}

/**
 * Service to determine whether the user has access to the "Regulations Next" module
 * and to build the regulation-details URL when they do.
 *
 * Used when user clicks INS_id from:
 * - v2.0 chatbot metadata
 * - v3.0 regulations agent metadata
 * - View all sources panel (v2.0 or v3.0)
 *
 * If user has gri.regulation.read → open
 * {base}/regulationsnext?modulePath=%2Fregulation-details%2FINSxxxxxx&source=chatbot
 * If not → open legacy URL (view-regulation?tab=true&insid=...)
 */
@Injectable({
  providedIn: 'root'
})
export class RegulationNextService {
  private privilegeNames: string[] = [];

  /**
   * Set the current user's privilege names (e.g. from getPrivilegesList cookiesArray
   * or AgentAccessControlService.getUserPrivileges()). Call this when privileges
   * are loaded (v2.0 setPrivilegesAndIds, v3.0 after getAvailableAgents).
   */
  setPrivileges(privilegeNames: string[]): void {
    this.privilegeNames = Array.isArray(privilegeNames) ? privilegeNames : [];
  }

  /**
   * Returns true if the user has the Regulations Next "View Regulation" privilege
   * (gri.regulation.read). When true, INS_id links should use the new
   * regulation-details URL format.
   */
  hasRegulationNextAccess(): boolean {
    return this.privilegeNames.includes(REGULATION_NEXT_PRIVILEGE);
  }

  /**
   * Build the Regulation Next URL for a given insight/regulation ID.
   * New format:
   * {baseUrl}/regulationsnext?modulePath=%2Fregulation-details%2F{insId}&source=chatbot
   * (e.g.
   * https://app.devsecops.freyrapps.com/regulationsnext?modulePath=%2Fregulation-details%2FINS206315&source=chatbot)
   *
   * With a version the details page opens that version:
   * {baseUrl}/regulationsnext?modulePath=%2Fregulation-details%2F{insId}?version={version}&source=chatbot
   *
   * @param baseUrl - e.g. configdata.FusionAppUIUrl (https://app.devsecops.freyrapps.com)
   * @param insId - regulation/insight ID (e.g. INS206315)
   * @param version - optional numeric version (e.g. 1.0); omitted when empty
   */
  buildRegulationNextUrl(
    baseUrl: string,
    insId: string,
    version?: string
  ): string {
    if (!baseUrl || !insId) {
      return '';
    }
    const base = baseUrl.replace(/\/$/, '');
    const modulePath = `/regulation-details/${encodeURIComponent(insId)}`;
    const trimmedVersion = (version ?? '').trim();
    const versionQuery = trimmedVersion ? `?version=${encodeURIComponent(trimmedVersion)}` : '';
    return `${base}/regulationsnext?modulePath=${encodeURIComponent(modulePath)}${versionQuery}&source=chatbot`;
  }

  /**
   * Build the legacy regulations view URL (used when user does not have
   * gri.regulation.read).
   */
  buildLegacyRegulationUrl(baseUrl: string, insId: string): string {
    if (!baseUrl || !insId) {
      return '';
    }
    const base = baseUrl.replace(/\/$/, '');
    return base + '/regulations?modulePath=/Insights/view-regulation?tab=true&insid=' + window.btoa(encodeURIComponent(insId));
  }
}
